import { useState, useRef, useId } from 'react';
import { LogIn, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { connector } from '@/lib/auth';
import { mapAuthErrorMessage } from '@/lib/auth-errors';
import { TextField } from './TextField';
import { Button } from './Button';

interface LoginGateProps {
  onLogin: () => void;
}

export function LoginGate({ onLogin }: LoginGateProps) {
  const onlineTabId = useId();
  const pinTabId = useId();
  const onlinePanelId = useId();
  const pinPanelId = useId();
  const tablistRef = useRef<HTMLDivElement>(null);

  const [mode, setMode] = useState<'online' | 'pin'>(
    typeof navigator !== 'undefined' && navigator.onLine ? 'online' : 'pin',
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  function focusTab(which: 'online' | 'pin') {
    setMode(which);
    const el = tablistRef.current?.querySelector<HTMLButtonElement>(
      which === 'online' ? `[id="${onlineTabId}"]` : `[id="${pinTabId}"]`,
    );
    el?.focus();
  }

  function onTabKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, which: 'online' | 'pin') {
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      focusTab(which === 'online' ? 'pin' : 'online');
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      focusTab(which === 'online' ? 'pin' : 'online');
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusTab('online');
    } else if (e.key === 'End') {
      e.preventDefault();
      focusTab('pin');
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError(null);
    try {
      if (mode === 'online') {
        await connector.loginOnline(email.trim(), password);
        toast.success('Sesión iniciada');
        onLogin();
      } else {
        const session = await connector.loginOffline(pin.trim());
        if (!session) {
          const pinMsg = 'PIN inválido o sin usuarios cacheados en este dispositivo';
          setFormError(pinMsg);
          toast.error(pinMsg);
          return;
        }
        toast.success(`Sesión offline: ${session.nombre ?? session.email}`);
        onLogin();
      }
    } catch (err: unknown) {
      const raw = err instanceof Error ? err.message : String(err);
      const msg = mapAuthErrorMessage(raw);
      setFormError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-[70vh] items-center justify-center">
      <div className="bg-neutral-0 w-full max-w-sm rounded-lg border border-neutral-200 p-6 shadow-[var(--shadow-ambient-low)]">
        <div className="mb-4 flex items-center gap-3">
          <div
            className="bg-primary-soft text-primary flex h-10 w-10 items-center justify-center rounded-md"
            aria-hidden="true"
          >
            <LogIn className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-neutral-900">Ingresar al POS</h2>
            <p className="text-xs text-neutral-500">Necesitás sesión para abrir caja y vender.</p>
          </div>
        </div>

        <div
          ref={tablistRef}
          role="tablist"
          aria-label="Modo de ingreso"
          className="mb-4 grid grid-cols-2 gap-1 rounded-md bg-neutral-100 p-1"
        >
          <button
            id={onlineTabId}
            type="button"
            role="tab"
            aria-selected={mode === 'online'}
            aria-controls={onlinePanelId}
            tabIndex={mode === 'online' ? 0 : -1}
            onClick={() => setMode('online')}
            onKeyDown={(e) => onTabKeyDown(e, 'online')}
            className={`flex items-center justify-center gap-1.5 rounded-sm px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)] ${
              mode === 'online'
                ? 'bg-neutral-0 text-neutral-900 shadow-[var(--shadow-ambient-low)]'
                : 'text-neutral-500 hover:text-neutral-700'
            }`}
          >
            <Wifi className="h-3.5 w-3.5" aria-hidden="true" /> Online
          </button>
          <button
            id={pinTabId}
            type="button"
            role="tab"
            aria-selected={mode === 'pin'}
            aria-controls={pinPanelId}
            tabIndex={mode === 'pin' ? 0 : -1}
            onClick={() => setMode('pin')}
            onKeyDown={(e) => onTabKeyDown(e, 'pin')}
            className={`flex items-center justify-center gap-1.5 rounded-sm px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus-ring)] ${
              mode === 'pin'
                ? 'bg-neutral-0 text-neutral-900 shadow-[var(--shadow-ambient-low)]'
                : 'text-neutral-500 hover:text-neutral-700'
            }`}
          >
            <WifiOff className="h-3.5 w-3.5" aria-hidden="true" /> PIN offline
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {formError && (
            <div
              role="alert"
              className="border-danger bg-danger-soft text-danger rounded-md border p-3 text-sm"
            >
              {formError}
            </div>
          )}
          {mode === 'online' ? (
            <div
              id={onlinePanelId}
              role="tabpanel"
              aria-labelledby={onlineTabId}
              className="space-y-3"
            >
              <TextField
                label="Email"
                type="email"
                required
                autoComplete="username"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="cajero@tienda.local"
              />
              <TextField
                label="Contraseña"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          ) : (
            <div id={pinPanelId} role="tabpanel" aria-labelledby={pinTabId}>
              <TextField
                label="PIN rápido (offline)"
                type="password"
                required
                inputMode="numeric"
                autoComplete="off"
                autoFocus
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                placeholder="••••"
                helperText="Requiere haber iniciado sesión online al menos una vez en este dispositivo."
                className="text-center text-lg tracking-widest"
              />
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            fullWidth
            loading={isSubmitting}
            loadingLabel="Ingresando…"
            leadingIcon={
              !isSubmitting ? <LogIn className="h-4 w-4" aria-hidden="true" /> : undefined
            }
          >
            Ingresar
          </Button>
        </form>
      </div>
    </div>
  );
}
